// services/questionService.ts
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  Timestamp,
  writeBatch
} from "firebase/firestore";
import { db } from "../firebase/config";
import { Question } from "../types";

const QUESTIONS_COLLECTION = "questions";

export const getQuestions = async (bankId?: string, traitId?: string): Promise<Question[]> => {
  try {
    let constraints = [];
    if (bankId) constraints.push(where("bankId", "==", bankId));
    if (traitId) constraints.push(where("traitId", "==", traitId));
    constraints.push(orderBy("createdAt", "desc"));
    const q = query(collection(db, QUESTIONS_COLLECTION), ...constraints);
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data(), 
      createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(), 
      updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString() 
    } as Question));
  } catch (error) { 
    console.error("Error fetching questions:", error); 
    throw error; 
  }
};

export const getQuestionById = async (id: string): Promise<Question | null> => {
  try {
    const docRef = doc(db, QUESTIONS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) return { id: docSnap.id, ...docSnap.data() } as Question;
    return null;
  } catch (error) { 
    console.error("Error fetching question:", error); 
    throw error; 
  }
};

export const createQuestion = async (questionData: Omit<Question, "id" | "createdAt" | "updatedAt">, userId: string): Promise<string> => {
  try {
    // Clean the data - remove any undefined values
    const cleanData: any = {};
    Object.keys(questionData).forEach(key => {
      const value = questionData[key as keyof typeof questionData];
      if (value !== undefined && value !== null) {
        cleanData[key] = value;
      }
    });
    
    const now = Timestamp.now();
    const docRef = await addDoc(collection(db, QUESTIONS_COLLECTION), {
      ...cleanData,
      createdBy: userId,
      createdAt: now,
      updatedAt: now,
    });
    await updateBankQuestionCount(questionData.bankId, "increment");
    return docRef.id;
  } catch (error) { 
    console.error("Error creating question:", error); 
    throw error; 
  }
};

export const updateQuestion = async (id: string, questionData: Partial<Omit<Question, "id" | "createdAt" | "updatedAt">>): Promise<void> => {
  try {
    // Clean the data - remove any undefined values
    const cleanData: any = {};
    Object.keys(questionData).forEach(key => {
      const value = questionData[key as keyof typeof questionData];
      if (value !== undefined && value !== null) {
        cleanData[key] = value;
      }
    });
    
    const docRef = doc(db, QUESTIONS_COLLECTION, id);
    await updateDoc(docRef, { ...cleanData, updatedAt: Timestamp.now() });
  } catch (error) { 
    console.error("Error updating question:", error); 
    throw error; 
  }
};

export const deleteQuestion = async (id: string, bankId: string): Promise<void> => {
  try {
    const docRef = doc(db, QUESTIONS_COLLECTION, id);
    await deleteDoc(docRef);
    await updateBankQuestionCount(bankId, "decrement");
  } catch (error) { 
    console.error("Error deleting question:", error); 
    throw error; 
  }
};

const updateBankQuestionCount = async (bankId: string, action: "increment" | "decrement"): Promise<void> => {
  try {
    const bankRef = doc(db, "questionBanks", bankId);
    const bankDoc = await getDoc(bankRef);
    if (bankDoc.exists()) {
      const currentCount = bankDoc.data().totalQuestions || 0;
      const newCount = action === "increment" ? currentCount + 1 : Math.max(0, currentCount - 1);
      await updateDoc(bankRef, { totalQuestions: newCount, updatedAt: Timestamp.now() });
    }
  } catch (error) { 
    console.error("Error updating bank count:", error); 
  }
};